import { isWeb, SizableText, Stack, YStack } from '@mythical/ui'
import { Banner } from 'app/blocks/banner'
import {
  BannerPattern,
  PATTERN_COLORS,
} from 'app/components/blocks/Ad/BannerPattern'
import { ButtonLink } from 'app/components/primitive/Button'
import RichText from 'app/components/primitive/RichText'
import { Container, ContainerProps } from 'app/components/structure/Container'
import { useStores } from 'app/store'
import React from 'react'
import { SolitoImage } from 'solito/image'

const RAINBOW_BG = require('app/assets/svg/rainbow.svg') as string

export interface AdRainbowProps extends ContainerProps {
  ctaLabel?: string
  description?: string
  href?: string
  image: string
  richTextDescription?: React.ReactNode
  title: string
  backgroundPattern?: Banner['backgroundPattern']
}

export const AdRainbow = ({
  backgroundPattern,
  ctaLabel,
  description,
  href,
  image,
  richTextDescription,
  title,
  ...props
}: AdRainbowProps) => {
  const { userStore } = useStores()
  const canMemberfulLink = userStore?.canMemberfulLink ?? false
  const hasPattern = backgroundPattern && backgroundPattern !== 'none'

  return (
    <Container paddedX variant="none" {...props}>
      <YStack
        borderRadius="$2"
        overflow="hidden"
        position="relative"
        zIndex={0}
        bg={hasPattern ? PATTERN_COLORS[backgroundPattern].bg : '$bgInverted'}
        $gtSm={{
          alignItems: 'center',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          gap: 0,
          justifyContent: 'space-between',
          px: '$8.5',
          py: '$6',
        }}
        $gtMd={{ px: '$6' }}
        $gtLg={{ px: '$8.5' }}
      >
        <YStack
          overflow="hidden"
          p="$3.5"
          position="relative"
          width="100%"
          $gtSm={{ display: 'none' }}
        >
          <Stack
            bottom={0}
            left={0}
            overflow="hidden"
            position="absolute"
            right={0}
            top={0}
          >
            <SolitoImage
              alt=""
              contentFit="contain"
              fill
              src={RAINBOW_BG}
              style={{
                transform: [
                  { scale: 2.25 },
                  { translateX: -72 },
                  { translateY: -34 },
                ],
              }}
            />
          </Stack>

          {!!image && (
            <Stack aspectRatio={16 / 9} borderRadius="$2" overflow="hidden">
              <SolitoImage
                alt=""
                contentFit="cover"
                fill
                sizes="(min-width: 800px) 548px, 100vw"
                src={image}
              />
            </Stack>
          )}
        </YStack>

        <YStack
          fg={1}
          flexBasis="45%"
          fs={1}
          gap="$3.5"
          pb="$3.5"
          px="$3.5"
          pt="$3"
          $gtSm={{ p: 0, pr: '$8.5', gap: '$6', pt: 0 }}
        >
          <YStack gap="$2" $gtSm={{ gap: '$3.5' }}>
            <SizableText
              color="$fgDarkGreen"
              ff="$display"
              tt="uppercase"
              $gtSm={{ size: '$l' }}
            >
              {title}
            </SizableText>
            {richTextDescription && (
              <RichText
                content={richTextDescription}
                textProps={{
                  color: '$fgDarkGreen',
                  size: '$s',
                  fontWeight: '500',
                  $gtSm: {
                    size: '$m',
                    fontWeight: '500',
                  },
                }}
              />
            )}
          </YStack>

          {canMemberfulLink && !!href && !!ctaLabel && (
            <ButtonLink
              alignSelf="flex-start"
              href={href}
              size="$4.5"
              textProps={{
                ff: '$title',
                fontWeight: '500',
              }}
              {...(href?.startsWith('http') ? { target: '_blank' } : {})}
            >
              {ctaLabel}
            </ButtonLink>
          )}
        </YStack>

        {!!image && (
          <Stack
            aspectRatio={16 / 9}
            borderRadius="$2"
            display="none"
            fg={1}
            flexBasis="55%"
            fs={1}
            overflow="hidden"
            $gtSm={{
              display: 'flex',
            }}
          >
            <SolitoImage
              alt=""
              contentFit="cover"
              fill
              sizes="(min-width: 800px) 548px, 100vw"
              src={image}
            />
          </Stack>
        )}

        {hasPattern && <BannerPattern backgroundPattern={backgroundPattern} />}

        <Stack
          aspectRatio={79 / 94}
          bottom={0}
          display="none"
          left={0}
          overflow="hidden"
          position="absolute"
          right={0}
          top={0}
          zIndex={-1}
          {...(isWeb ? { width: '100%', height: '100%' } : {})}
          $gtSm={{
            display: 'flex',
          }}
        >
          <SolitoImage
            alt=""
            contentFit="contain"
            fill
            src={RAINBOW_BG}
            style={{
              transform: [
                { scale: 2 },
                { translateX: 75 },
                { translateY: -25 },
              ],
            }}
          />
        </Stack>
      </YStack>
    </Container>
  )
}
